export const paymentAccounts = {
  // Stripe and Wompi are TWO integrations, not two flavours of one:
  // each has its own screen and its own header. `tagline` is the line
  // that places you (where it collects and with which methods).
  header: {
    stripe: {
      title: "Payouts with Stripe",
      tagline: "International cards · Spain and most other countries",
      description:
        "Connect one or more Stripe accounts and choose which one collects for each listing.",
    },
    wompi: {
      title: "Payouts with Wompi",
      tagline: "Colombia · PSE, Nequi and Colombian cards",
      description:
        "Connect one or more Wompi accounts and choose which one collects for each listing.",
    },
    bold: {
      title: "Payments with Bold",
      tagline: "Colombia and Peru · PSE, Nequi, Bancolombia and cards",
      description:
        "Connect one or more Bold accounts and choose which one charges each listing.",
    },
    redsys: {
      title: "Payments with Redsys",
      tagline: "Spain · Your bank's virtual POS",
      description:
        "Connect your bank's virtual POS and choose which account each listing charges with. The money goes straight to your bank account.",
      card: "Your bank's virtual POS (Spain)",
    },
  },

  // Wompi where it does not apply. The gateway is decided by the
  // LISTING's country, not the owner's: that is why this talks about
  // properties, not about you.
  notAvailable: {
    title: "Wompi is for properties in Colombia",
    text: "Wompi collects in Colombian pesos through PSE, Nequi and Colombian cards. You don't have any property there, so there is nothing to connect here.",
    hint: "If you list a property in Colombia, this screen will open on its own.",
    button: "Go to {provider}",
  },

  session: {
    title: "Your session has expired",
    text: "Nothing has been lost: your payment accounts are still connected. Log in again to see them.",
    button: "Log in again",
  },

  status: {
    ready: "Ready",
    incomplete: "Incomplete",
    unverified: "Not verified",
  },

  environment: {
    test: "Test",
  },

  accounts: {
    card: {
      title: "Your {provider} accounts",
      subtitle: "Connect new accounts and check whether they are ready to collect.",
    },
    searchPlaceholder: "Search accounts by name…",
    newButton: "Connect {provider} account",
    noName: "Unnamed",
    fallbackName: "Account #{id}",
    defaultBadge: "Main",
    listingsCount: "{count} listing | {count} listings",
    empty: {
      none: "You haven't connected any {provider} account yet.",
      noMatch: "No account matches your search.",
    },
    actions: {
      edit: "Edit",
      setDefault: "Make it the main one",
      isDefault: "Already the main one",
      resume: "Finish setup",
      verify: "Verify",
      disconnect: "Disconnect",
    },
  },

  assign: {
    card: {
      title: "Listing by listing",
      subtitle: "Choose which listings each {provider} account collects for.",
    },
    searchPlaceholder: "Search listings by name…",
    filter: {
      all: "All",
      unassigned: "Unassigned",
      notReady: "Can't collect",
      other: "Collects through {provider}",
    },
    // No account assigned. When the main one is known it gets named, which is
    // what the host wants to know: which account collects for this listing.
    autoOption: "Main account",
    autoOptionNamed: "Main account · {name}",
    autoOptionOther: "Main {provider} account · {name}",
    otherProvider: "Collects through {provider} · {name}",
    otherProviderLink: "Manage in {provider}",
    notReadyRow: "Can't collect",
    notReadyRowTip: "The account assigned to this listing isn't ready yet, so guests won't be able to pay.",
    notReadyAutoRow: "Your main account can't collect",
    notReadyAutoRowTip: "This listing uses the main account, which isn't ready yet. Finish it or assign the listing another account.",
    notReadyBanner: "{count} listing can't collect right now: its account isn't ready yet. Guests won't be able to pay for it until you finish that account. | {count} listings can't collect right now: their account isn't ready yet. Guests won't be able to pay for them until you finish that account.",
    notReadyBannerLink: "See those listings",
    defaultSuffix: " (main)",
    empty: {
      none: "You don't have any listings yet.",
      noMatch: "No listing matches the filter.",
    },
    count: "Showing {shown} of {total} listings",
  },

  drawer: {
    newTitle: "Connect a {provider} account",
    editTitle: "Edit the {provider} account",
    subtitle: "Give it a name and choose which listings it collects for.",
    closeAria: "Close the panel",

    stripe: {
      title: "Stripe onboarding",
      subtitle: "Stripe will ask for your details and bring you back here.",
      explain:
        "We'll take you to Stripe to create the account under this name and verify your details. When you come back you can pick the listings it collects for; if you leave the setup halfway, the account waits here so you can finish it.",
      button: "Continue on Stripe",
      connecting: "Opening Stripe…",
      resumeTitle: "Stripe setup unfinished",
      resumeSubtitle: "The verification on Stripe is still pending.",
      resumeExplain: "This account was created but never finished its Stripe setup, so it can't collect yet. Pick up where you left off.",
      resumeButton: "Finish setup",
    },

    data: {
      title: "Account details",
      subtitle: "The name is only for you: it helps you tell them apart.",
      label: "Account name",
      labelPlaceholder: "For example: Beach apartments",
      labelHint: "Guests never see it.",
      publicKey: "Public key",
      privateKey: "Private key",
      privateKeyKept: "The one you already had is kept",
      privateKeyHint: "Leave it empty to keep the saved key.",
      stripeId: "Stripe identifier",
    },

    listings: {
      title: "Listings it collects for",
      subtitle: "The ones you tick will stop collecting through their previous account.",
      label: "Listings on this account",
      selectedCount: "{selected} of {total} selected",
      searchPlaceholder: "Search listings…",
      selectAll: "Select all",
      clear: "Clear",
      takenBy: "now on {name}",
      noResults: "No listing matches your search.",
      hint: "Listings you don't tick on any account collect through the main one.",
      otherHidden: "{count} listing that collects through {provider} is not shown here: manage it on its own screen. | {count} listings that collect through {provider} are not shown here: manage them on their own screen.",
    },

    footer: {
      isDefault: "Already the main one",
      setDefault: "Make it the main one",
      disconnect: "Disconnect",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
    },
  },

  disconnect: {
    title: "Disconnect the account",
    subtitle: "You will stop collecting with it.",
    confirm:
      "You're about to disconnect “{name}”. Bookings already charged aren't affected, but this account will stop taking new payments.",
    blocked:
      "“{name}” still collects for {count} listing. It has to move to another account before you can disconnect it. | “{name}” still collects for {count} listings. They have to move to another account before you can disconnect it.",
    moveLabel: "Move those listings to",
    moveHint: "We move them to the account you pick and then disconnect this one.",
    cancel: "Cancel",
    accept: "Disconnect",
    seeListings: "See those listings",
    moveAndDisconnect: "Move and disconnect",
  },

  loading: {
    accounts: "Loading your payout accounts…",
  },

  msg: {
    loadError: "We couldn't load your payout accounts.",
    labelRequired: "Give the account a name.",
    publicKeyRequired: "The Wompi public key is missing.",
    privateKeyRequired: "The Wompi private key is missing.",
    saved: "Account saved.",
    saveError: "We couldn't save the account.",
    stripeNoUrl: "Stripe didn't return the onboarding link. Please try again.",
    stripeConnectError: "We couldn't open Stripe onboarding.",
    verified: "The account is verified.",
    verifiedWithName: "Account verified: {name}.",
    notVerified: "The account isn't verified yet. Check the keys.",
    verifyError: "We couldn't verify the account.",
    defaultUpdated: "It's now the main account.",
    defaultError: "We couldn't change the main account.",
    disconnected: "Account disconnected.",
    disconnectError: "We couldn't disconnect the account.",
    movedAndDisconnected: "Listings moved and account disconnected.",
    savedNotReady: "Careful: this account can't collect yet and has {count} listing assigned. Finish it or that listing won't get paid. | Careful: this account can't collect yet and has {count} listings assigned. Finish it or those listings won't get paid.",
    assignError: "We couldn't change the account for this listing.",
  },

  integrations: {
    connectedAccounts: "{count} account connected | {count} accounts connected",
    notApplicable: "For properties in Colombia",
    notApplicableSpain: "For properties in Spain",
  },

  // Redsys: los tres datos del TPV que da el banco, y el entorno.
  redsys: {
    environment: "Environment",
    environmentHint: "Your bank gives you one set of details for testing and another for real payments. Choose which one you are entering.",
    env: {
      test: "Test",
      prod: "Live",
    },
    merchantCode: "Merchant number (FUC)",
    merchantCodeHint: "It is in your POS contract or in the Redsys administration module. Digits only, usually nine.",
    terminal: "Terminal",
    terminalHint: "Almost always 1. If your bank gave you several, use the virtual POS one.",
    secretKey: "Signing key (SHA-256)",
    secretKeyHint: "In the Redsys administration module → Merchant details → “View key”. We check it with Redsys when you save, without charging anything.",
    onlyEuro: "Euros only · charges in {currency}",
    onlyEuroTip: "Redsys only charges in euros. Assign this property to another payment account.",
    msg: {
      merchantCode: "The merchant number is digits only (usually nine).",
      terminal: "The terminal is a number of one to three digits (almost always 1).",
      secretKey: "The Redsys signing key is missing.",
    },
  },
};
